import { Flower2, Hourglass, Users, Compass, Map, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const IMG =
  "https://images.unsplash.com/photo-1552838671-0e948f9f69b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxtYXR1cmUlMjB3b21hbiUyMHJlbGF4aW5nJTIwc3Vuc2V0JTIwbmF0dXJlJTIwdGhvdWdodGZ1bHxlbnwwfHx8fDE3OTIxNzE0NTN8MA&ixlib=rb-4.1.0&q=85";

const TAKEAWAYS = [
  { icon: Flower2, text: "Greater understanding of your menopause journey and the questions worth exploring." },
  { icon: Hourglass, text: "Time and space for yourself to pause and reflect without your usual daily pressures." },
  { icon: Users, text: "Meaningful connection with women who may share similar experiences." },
  { icon: Compass, text: "A clearer sense of your priorities, wellbeing needs and what matters to you now." },
  { icon: Map, text: "Personal reflections and next steps to help you approach your next chapter with greater intention." },
];

export const Outcomes = ({ onBookClick }) => (
  <section data-testid="outcomes-section" className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32">
    <Reveal>
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#A34A6B]">What you'll take away</p>
      <h2 className="max-w-2xl font-serif text-2xl leading-snug text-cream sm:text-3xl lg:text-4xl">
        The intention is for you to <span className="italic text-[#A34A6B]">leave with:</span>
      </h2>
    </Reveal>

    <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
      {TAKEAWAYS.map((t, i) => (
        <Reveal key={t.text} delay={i * 0.08}>
          <div
            data-testid={`takeaway-card-${i + 1}`}
            className="h-full rounded-3xl border border-[rgba(163,74,107,0.15)] bg-white/70 p-7 transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-1 hover:border-[#A34A6B]/40 hover:shadow-[0_20px_60px_rgba(163,74,107,0.12)]"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F3D9DC]">
              <t.icon className="h-5 w-5 text-[#A34A6B]" />
            </span>
            <p className="mt-5 text-sm leading-relaxed text-lilac">{t.text}</p>
          </div>
        </Reveal>
      ))}
      <Reveal delay={0.4}>
        <div className="group relative h-full min-h-[220px] overflow-hidden rounded-3xl border border-[rgba(163,74,107,0.2)]">
          <img
            src={IMG}
            alt="A woman reflecting quietly in nature at sunset"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(47,27,54,0.55),transparent_55%)]" />
        </div>
      </Reveal>
    </div>

    <Reveal delay={0.15}>
      <p className="mx-auto mt-12 max-w-2xl text-center font-serif text-lg italic leading-relaxed text-[#A34A6B]/90">
        You don't need to arrive with a plan. You don't need to have everything figured out. This is your
        opportunity to begin where you are.
      </p>
    </Reveal>

    <div className="mt-20 grid items-start gap-12 lg:grid-cols-2 lg:gap-20">
      <Reveal>
        <h3 className="font-serif text-2xl leading-snug text-cream sm:text-3xl">A space created with you in mind</h3>
        <p className="mt-5 text-base leading-relaxed text-lilac">
          The Menopause Reclaim Retreat has been designed especially for high-achieving professional women,
          executives and entrepreneurs who are accustomed to carrying a great deal and who recognise the
          importance of investing in themselves, too.
        </p>
        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-mutedlilac">Our guiding philosophy is simple:</p>
        <p className="mt-3 font-serif text-2xl text-[#A34A6B] sm:text-3xl">Understand. Reset. Reclaim.</p>
        <p className="mt-6 border-l-2 border-[#A34A6B]/40 pl-5 font-serif text-lg italic text-[#A34A6B]/90">
          Because menopause doesn't come with a manual. We give women a roadmap.
        </p>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="rounded-3xl border border-[rgba(163,74,107,0.18)] bg-[#F9F3EE] p-8 sm:p-10">
          <h3 className="font-serif text-2xl leading-snug text-cream sm:text-3xl">
            Your next chapter starts with a <span className="italic text-[#A34A6B]">pause</span>
          </h3>
          <p className="mt-5 text-sm leading-relaxed text-lilac">
            You have spent years investing in your career, your ambitions and the people around you. Now, give
            yourself permission to invest time in your own wellbeing and future.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-lilac">
            Ready to explore whether the Menopause Reclaim Retreat is right for you? Book a discovery call with
            Marie to discuss your needs, learn more about the retreat vision and explore whether the experience
            aligns with what you are looking for.
          </p>
          <button
            type="button"
            onClick={onBookClick}
            data-testid="discovery-call-button"
            className="group mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#A34A6B] px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-[#FDF6F2] transition-[background-color,transform,box-shadow] duration-300 hover:bg-[#8E3D5B] hover:shadow-[0_0_36px_rgba(163,74,107,0.3)] active:scale-[0.98]"
          >
            Book a discovery call
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);
